import { BentoGrid } from "@/components/ui/bento-grid";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Code,
  PenTool,
  Zap,
  Users,
  Monitor,
  LightbulbIcon,
} from "lucide-react";
import React from "react";
import {
  Section,
  SectionContainer,
  SectionHeader,
  SectionTitle,
  SectionContent,
} from "@/components/ui/section";

const values = [
  {
    icon: <Code />,
    color: "blue",
    title: "Clean Code",
    description:
      "Tworzę przejrzysty, modulowy kod zgodny z najlepszymi praktykami, co ułatwia jego utrzymanie i rozwój w przyszłości.",
  },
  {
    icon: <PenTool />,
    color: "green",
    title: "User-Centered Design",
    description:
      "Projektuję z myślą o użytkownikach, stawiając ich potrzeby i doświadczenia w centrum procesu twórczego.",
  },
  {
    icon: <Zap />,
    color: "yellow",
    title: "Performance First",
    description:
      "Optymalizuję każdy aspekt witryny dla maksymalnej wydajności, co przekłada się na lepsze doświadczenia użytkownika i wyższe konwersje.",
  },
  {
    icon: <Users />,
    color: "red",
    title: "Collaborative Approach",
    description:
      "Wierzę w ścisłą współpracę z klientami i zespołami projektowymi, co zapewnia lepsze rezultaty i satysfakcję wszystkich stron.",
  },
  {
    icon: <Monitor />,
    color: "purple",
    title: "Responsive by Default",
    description:
      "Wszystkie moje projekty są w pełni responsywne, zapewniając doskonałe doświadczenia na każdym urządzeniu i rozmiarze ekranu.",
  },
  {
    icon: <LightbulbIcon />,
    color: "orange",
    title: "Continuous Learning",
    description:
      "Nieustannie rozwijam swoje umiejętności, śledząc najnowsze trendy i technologie w dynamicznie zmieniającym się świecie web developmentu.",
  },
];

const About = () => {
  return (
    <Section id="about" className="relative overflow-hidden">
      <SectionContainer>
        {/* Section header */}
        <SectionHeader>
          <SectionTitle>About Me</SectionTitle>
        </SectionHeader>

        <SectionContent className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-10">
          {/* Main content - 7 columns on large screens */}
          <div className="lg:col-span-7 order-last lg:order-first">
            <div className="text-lg leading-relaxed text-muted-foreground space-y-5 mb-8">
              <p>
                I'm <strong className="text-primary">Jakub Wawrysiewicz</strong>
                , a passionate Web Front-End Developer with a deep commitment to
                creating exceptional digital experiences.
              </p>
              <p>
                With a background in both design and development, I bridge the
                gap between aesthetics and functionality, creating websites that
                not only look beautiful but also perform flawlessly.
              </p>
              <p>
                My expertise lies in modern front-end technologies, crafting
                responsive and high-performance applications that enhance user
                experience and accessibility. I take pride in writing clean,
                semantic code that's built to last.
              </p>
              <p>
                What sets me apart is my holistic understanding of the web
                development process—from initial concept to final
                deployment—allowing me to deliver cohesive, polished products
                that exceed expectations.
              </p>
            </div>

            <Button asChild size="lg" className="group mb-8">
              <a href="#contact" className="inline-flex items-center gap-2">
                Let's Work Together
                <ArrowRight
                  className="-me-2 ms-0 transition-transform group-hover:translate-x-0.5"
                  size={16}
                  strokeWidth={2}
                  aria-hidden="true"
                />
              </a>
            </Button>
          </div>

          {/* Image and quick facts - 5 columns on large screens */}
          <div className="lg:col-span-5 space-y-8">
            <div className="relative w-full max-w-md mx-auto lg:max-w-full">
              <div className="rounded-xl overflow-hidden shadow-lg relative">
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-primary/20 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-primary/20 rounded-full blur-2xl"></div>

                <img
                  src="/images/me2-desktop.jpg"
                  alt="Jakub's portrait"
                  className="object-cover w-full h-full aspect-[4/5] grayscale transition duration-500 hover:grayscale-0 relative z-10"
                />

                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70 z-20"></div>

                {/* Name overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 z-30">
                  <h3 className="text-white text-2xl font-medium mb-1">
                    Jakub Wawrysiewicz
                  </h3>
                  <p className="text-white/80 text-sm">
                    Front-End Developer & UI Designer
                  </p>
                </div>
              </div>
            </div>
          </div>
        </SectionContent>
        {/* Values section */}
        <div className="mb-8">
          <h3 className="text-xl font-medium mb-4 flex items-center gap-2">
            My Core Values & Philosophy
          </h3>
          <BentoGrid items={values} variant="default" columns={2} />
        </div>
      </SectionContainer>
    </Section>
  );
};

export default About;
